import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { SingleCampaignAdgroupSelector, MultiCampaignAdgroupSelector } from './selector-control';
import { MultiSelectedAdgroupItemComponent } from './components/multi-selected-adgroup-item';

/**
 * A business component of campaign adgroup selector based on cascader
 * Provide a component with funtion of selecting an item from some campaigns and adgroups
 * support single mode and multi mode
 */

export class CampaignAdgroupSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isToggled: false,
      selectedItem: props.selectedAdGroup,
      resultList: [],
    };

    this.resultContainerOptions = this.getResultContainerOptions();
  }

  /**
   * for the multi mode: after the Done btn clicked, should toggle the dropdown, and call back
   * @param {object} items - selected result adgroups
   * @returns {undefined} do not need return
   */
  onSelectDone = (items) => {
    this.toggle();
    this.props.onSelectDone(items);
  }

  /**
   * for the multi mode: after the Cancel btn clicked, should toggle the dropdown, and call back
   * @returns {undefined} do not need return
   */
  onSelectCancel = () => {
    this.toggle();
    this.props.onSelectCancel();
  }

  /**
   * for multi mode: dropdown btn will show lick this: ** items selected
   * @returns {selectedItem} return an item which can be used for dropdown button to show
   */
  getSelectedItemForMultiMode = () => {
    const resultListLen = this.state.resultList.length;
    const selectedText = this.getHeaderTxt(resultListLen);
    return resultListLen ? { name: selectedText } : null;
  };

  /**
   * for multi mode: prepare the params for the withDropdownMenu hoc
   * @returns {undefined} do not need return
   */
  getDropdownOptions = () => {
    const { i18n, isMultiMode } = this.props;

    const renderDropdownText = item => (
      isMultiMode ?
        <span className="dropdown-edit-mode-text" title={item.name}>
          {item.name}
        </span> :
        <span>
          <span className={classnames('dropdown-edit-mode-parent-text', 'dropdown-edit-mode-text')}>
            {item.campaignName}
          </span>
          <span className="dropdown-edit-mode-text">
            {'>'}
          </span>
          <span className="dropdown-edit-mode-text">
            {item.name}
          </span>
        </span>
    );

    const renderEmptyDropdownText = () => {
      const { stringKeys } = this.props;
      if (isMultiMode) {
        return stringKeys && stringKeys.selectEntities ?
          i18n.getString(stringKeys.selectEntities)
          : i18n.getString(_TL_('Select ad group(s)'));
      }
      return stringKeys && stringKeys.selectEntity ?
        i18n.getString(stringKeys.selectEntity)
        : i18n.getString(_TL_('Select an ad group'));
    };

    return {
      isToggled: this.state.isToggled,
      toggle: this.toggle,
      renderDropdownText,
      renderEmptyDropdownText,
      selectedItem: isMultiMode ? this.getSelectedItemForMultiMode() : this.state.selectedItem,
      updatedDropdownItem: this.updatedDropdownItem,
      focusFirstItemOnMount: true,
      ariaLabel: renderEmptyDropdownText(),
    };
  }

  getHeaderTxt = (itemCount) => {
    const { i18n, stringKeys } = this.props;
    return stringKeys && stringKeys.resultHeader ?
      i18n.getString(stringKeys.resultHeader, { itemCount })
      : i18n.getString(_TL_('Selected ad groups: {{itemCount}}'), { itemCount });
  };

  /**
   * for multi mode: prepare the params for the withMultiResultContainer hoc
   * @returns {undefined} do not need return
   */
  getResultContainerOptions = () => {
    const renderHeader = (itemCount) => {
      const headerTxt = this.getHeaderTxt(itemCount);
      return (
        <div className="panel-heading">
          <h4 className="panel-title">
            {headerTxt}
          </h4>
        </div>);
    };

    const renderEmptyList = emptyTips => (
      <div className="empty-content">
        {emptyTips}
      </div>
    );

    const renderItem = (item, index) =>
      (<MultiSelectedAdgroupItemComponent item={item} index={index} />);

    const onResultChanged = (resultList) => {
      this.setState({ resultList });
    };

    return {
      renderHeader,
      renderEmptyList,
      renderItem,
      onSelectDone: this.onSelectDone,
      onSelectCancel: this.onSelectCancel,
      onResultChanged,
    };
  }

  /**
    * will update the seleted item info in the dropdown btn
    * @param {object} selectedItem the item selected
    * @returns {undefined} update the Dropdown btn info
    */
  updatedDropdownItem = (selectedItem) => {
    this.setState({ selectedItem });
  }

  /**
   * control the toggle state
   * @returns {undefined} update the state
   */
  toggle = () => {
    this.setState({ isToggled: !this.state.isToggled });
  }

  render() {
    const { isMultiMode } = this.props;
    return (
      <div className={classnames('ba-campaign-adgroup-selector')}>
        {
          isMultiMode ?
            <MultiCampaignAdgroupSelector
              {...this.props}
              dropdownOptions={this.getDropdownOptions()}
              resultContainerOptions={this.resultContainerOptions}
            /> :
            <SingleCampaignAdgroupSelector
              {...this.props}
              dropdownOptions={this.getDropdownOptions()}
            />
        }
      </div>
    );
  }
}

CampaignAdgroupSelector.propTypes = {
  /**
    * the campaign options for selector
    */
  campaign: PropTypes.shape({
    onFetch: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    checkSelected: PropTypes.func,
  }).isRequired,
  /**
    * the adgroup options for selector
    */
  adgroup: PropTypes.shape({
    onFetch: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    checkSelected: PropTypes.func,
  }).isRequired,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * keys for customized strings
   */
  stringKeys: PropTypes.objectOf(PropTypes.string),
  /**
   * initial selected campaign
   */
  selectedCampaign: PropTypes.objectOf(PropTypes.any),
  /**
    * initial selected adgroup
    */
  selectedAdGroup: PropTypes.objectOf(PropTypes.any),
  /**
    * the campaign adgroup selector mode: multi or single
    */
  isMultiMode: PropTypes.bool,
  /**
   * the initial selected adgroup list for multi mode
   */
  selectedAdgroupsForMultiMode: PropTypes.arrayOf(PropTypes.any),
  /**
    * callback for the Done btn clicked, with the selected items
    */
  onSelectDone: PropTypes.func,
  /**
    * callback for the Cancel btn clicked, with the selected items
    */
  onSelectCancel: PropTypes.func,
};

CampaignAdgroupSelector.defaultProps = {
  selectedCampaign: null,
  selectedAdGroup: null,
  stringKeys: null,
  isMultiMode: false,
  selectedAdgroupsForMultiMode: [],
  onSelectDone: _.noop,
  onSelectCancel: _.noop,
};

